/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, TableInfoSpec, TableSpec } from "../shared/contracts";
import type { IAnimatable } from "./core/ianimatable";
import { isAnimatable } from "./core/ianimatable";
import type { IHittable } from "./core/ihittable";
import { isHittable } from "./core/ihittable";
import type { IMovable } from "./core/imovable";
import { isMovable } from "./core/imovable";
import type { IPlayable } from "./core/iplayable";
import { isPlayable } from "./core/iplayable";
import type { IFrameSource, Item } from "./core/item";
import { isFrameSource } from "./core/item";
import type { ItemData } from "./core/item-data";
import { Material } from "./core/material";
import { Bumper } from "./items/bumper/bumper";
import { BumperData } from "./items/bumper/bumper-data";
import { Decoration, DecorationData } from "./items/decoration/decoration";
import { Flipper } from "./items/flipper/flipper";
import { FlipperData } from "./items/flipper/flipper-data";
import { Gate } from "./items/gate/gate";
import { GateData } from "./items/gate/gate-data";
import { Kicker } from "./items/kicker/kicker";
import { KickerData } from "./items/kicker/kicker-data";
import { Light } from "./items/light/light";
import { LightData } from "./items/light/light-data";
import { Plunger } from "./items/plunger/plunger";
import { PlungerData } from "./items/plunger/plunger-data";
import { Ramp } from "./items/ramp/ramp";
import { RampData } from "./items/ramp/ramp-data";
import { Rubber } from "./items/rubber/rubber";
import { RubberData } from "./items/rubber/rubber-data";
import { Spinner } from "./items/spinner/spinner";
import { SpinnerData } from "./items/spinner/spinner-data";
import { Surface } from "./items/surface/surface";
import { SurfaceData } from "./items/surface/surface-data";
import { HitTarget } from "./items/target/hit-target";
import { HitTargetData } from "./items/target/hit-target-data";
import { Trigger } from "./items/trigger/trigger";
import { TriggerData } from "./items/trigger/trigger-data";
import { degToRad, f4 } from "./math/float";
import { FRect3D } from "./math/frect3d";
import { Vertex2D } from "./math/vertex2d";
import { Vertex3D } from "./math/vertex3d";
import { Hit3DPoly } from "./physics/hit-3dpoly";
import type { HitObject } from "./physics/hit-object";
import { HitPlane } from "./physics/hit-plane";
import { LineSeg } from "./physics/line-seg";

/**
 * Global data about the table.
 *
 * Upstream the data comes from a binary file. Here it comes from `TableSpec.info`.
 */
export class TableData {
  public name: string;
  public left: number;
  public top: number;
  public right: number;
  public bottom: number;
  public glassHeight: number;
  public tableHeight: number;
  /** Degrees. */
  public angletiltMin: number;
  /** Degrees. */
  public angleTiltMax: number;
  /** 0 to 1. Picks a slope between the minimum and the maximum. */
  public globalDifficulty: number;
  public gravity: number;
  public friction: number;
  public elasticity: number;
  public elasticityFalloff: number;
  /** Degrees. */
  public scatter: number;
  public plungerNormalize: number;
  public plungerFilter: boolean;
  public ballRadius: number;
  public ballMass: number;

  constructor(info: TableInfoSpec) {
    this.name = info.name;
    this.left = info.left;
    this.top = info.top;
    this.right = info.right;
    this.bottom = info.bottom;
    this.glassHeight = info.glassHeight;
    this.tableHeight = info.tableHeight ?? 0;
    this.angletiltMin = info.angletiltMin;
    this.angleTiltMax = info.angleTiltMax;
    this.globalDifficulty = info.globalDifficulty;
    this.gravity = info.gravity;
    this.friction = info.friction;
    this.elasticity = info.elasticity;
    this.elasticityFalloff = info.elasticityFalloff;
    this.scatter = info.scatter;
    this.plungerNormalize = info.plungerNormalize ?? 100;
    this.plungerFilter = info.plungerFilter ?? false;
    this.ballRadius = info.ballRadius;
    this.ballMass = info.ballMass;
  }

  public getName(): string {
    return this.name;
  }

  public getFriction(): number {
    return this.friction;
  }

  public getElasticity(): number {
    return this.elasticity;
  }

  public getElasticityFalloff(): number {
    return this.elasticityFalloff;
  }

  public getScatter(): number {
    return this.scatter;
  }
}

type ItemMap<T> = { [key: string]: T };

/**
 * A table. It holds the items that the engine runs.
 *
 * Upstream a loader fills the table from a file. Here `Table.fromSpec` builds
 * it from a `TableSpec`. There is no script and no file loader.
 */
export class Table {
  public readonly data: TableData;
  public readonly spec: TableSpec;
  public readonly items: ItemMap<Item<ItemData>> = {};
  public readonly materials: Map<string, Material> = new Map();

  public readonly bumpers: ItemMap<Bumper> = {};
  public readonly flippers: ItemMap<Flipper> = {};
  public readonly gates: ItemMap<Gate> = {};
  public readonly hitTargets: ItemMap<HitTarget> = {};
  public readonly kickers: ItemMap<Kicker> = {};
  public readonly lights: ItemMap<Light> = {};
  public readonly ramps: ItemMap<Ramp> = {};
  public readonly rubbers: ItemMap<Rubber> = {};
  public readonly spinners: ItemMap<Spinner> = {};
  public readonly surfaces: ItemMap<Surface> = {};
  public readonly triggers: ItemMap<Trigger> = {};
  public readonly decorations: ItemMap<Decoration> = {};
  public readonly plunger: Plunger;

  public static playfieldThickness = 20.0;

  /**
   * Builds the engine items of a table.
   *
   * @throws Error when two items share a name.
   */
  public static fromSpec(spec: TableSpec): Table {
    return new Table(spec);
  }

  private constructor(spec: TableSpec) {
    this.spec = spec;
    this.data = new TableData(spec.info);
    const info = spec.info;

    const specMaterials = new Map<string, MaterialSpec>();
    for (const m of spec.materials) {
      specMaterials.set(m.name, m);
      this.materials.set(m.name, Material.fromSpec(m));
    }

    const add = <T extends Item<ItemData>>(map: ItemMap<T>, item: T): T => {
      const name = item.getName();
      if (this.items[name]) {
        throw new Error(`Table "${spec.id}": two items have the name "${name}".`);
      }
      this.items[name] = item;
      map[name] = item;
      return item;
    };

    for (const s of spec.flippers) add(this.flippers, new Flipper(FlipperData.fromSpec(s, info)));
    for (const s of spec.bumpers) add(this.bumpers, new Bumper(BumperData.fromSpec(s, info)));
    for (const s of spec.walls) {
      add(this.surfaces, new Surface(SurfaceData.fromSpec(s, info, specMaterials)));
    }
    for (const s of spec.rubbers) {
      add(this.rubbers, new Rubber(RubberData.fromSpec(s, info, specMaterials)));
    }
    for (const s of spec.ramps)
      add(this.ramps, new Ramp(RampData.fromSpec(s, info, specMaterials)));
    for (const s of spec.kickers) add(this.kickers, new Kicker(KickerData.fromSpec(s, info)));
    for (const s of spec.triggers) add(this.triggers, new Trigger(TriggerData.fromSpec(s, info)));
    for (const s of spec.gates) add(this.gates, new Gate(GateData.fromSpec(s, info)));
    for (const s of spec.spinners) add(this.spinners, new Spinner(SpinnerData.fromSpec(s, info)));
    this.plunger = add({}, new Plunger(PlungerData.fromSpec(spec.plunger, info)));
    for (const s of spec.lights) add(this.lights, new Light(LightData.fromSpec(s, info)));
    for (const s of spec.targets) {
      add(this.hitTargets, new HitTarget(HitTargetData.fromSpec(s, info, specMaterials)));
    }
    for (const s of spec.decorations) {
      const data = DecorationData.fromSpec(s, info);
      if (data) {
        add(this.decorations, new Decoration(data));
      }
    }
  }

  public getName(): string {
    return this.data.getName();
  }

  public getMaterial(name?: string): Material | undefined {
    if (!name) {
      return undefined;
    }
    return this.materials.get(name);
  }

  public getBoundingBox(): FRect3D {
    return new FRect3D(
      this.data.left,
      this.data.right,
      this.data.top,
      this.data.bottom,
      this.getTableHeight(),
      this.data.glassHeight,
    );
  }

  public getPlayables(): IPlayable[] {
    return this.getItems().filter(isPlayable) as unknown as IPlayable[];
  }

  public getMovables(): IMovable[] {
    return this.getItems().filter(isMovable) as unknown as IMovable[];
  }

  public getAnimatables(): IAnimatable[] {
    return this.getItems().filter(isAnimatable) as unknown as IAnimatable[];
  }

  public getHittables(): IHittable[] {
    return this.getItems().filter(isHittable) as unknown as IHittable[];
  }

  /** The items that have run-time values for the renderer. */
  public getFrameSources(): IFrameSource[] {
    return this.getItems().filter(isFrameSource) as unknown as IFrameSource[];
  }

  /** The outer borders of the table. */
  public getHitShapes(): HitObject[] {
    return generateOuterBorders(this.data);
  }

  public generatePlayfieldHit() {
    return new HitPlane(new Vertex3D(0, 0, 1), this.data.tableHeight)
      .setFriction(this.data.getFriction())
      .setElasticity(this.data.getElasticity(), this.data.getElasticityFalloff())
      .setScatter(degToRad(this.data.getScatter()));
  }

  public generateGlassHit() {
    return new HitPlane(new Vertex3D(0, 0, -1), this.data.glassHeight).setElasticity(0.2);
  }

  public getScaleZ(): number {
    return 1.0;
  }

  public getDetailLevel() {
    return 10;
  }

  public getGlobalDifficulty(): number {
    return this.data.globalDifficulty;
  }

  public getTableHeight() {
    return this.data.tableHeight;
  }

  public getDimensions(): { width: number; height: number } {
    return {
      width: this.data.right - this.data.left,
      height: this.data.bottom - this.data.top,
    };
  }

  /** The height of the surface at a point. A surface is a wall or a ramp. Unset: the playfield. */
  public getSurfaceHeight(surface: string | undefined, x: number, y: number) {
    if (!surface) {
      return this.data.tableHeight;
    }

    const wall = this.surfaces[surface];
    if (wall) {
      return f4(this.data.tableHeight + wall.heightTop);
    }

    const ramp = this.ramps[surface];
    if (ramp) {
      return f4(this.data.tableHeight + ramp.getSurfaceHeight(x, y, this));
    }

    // an unknown surface name counts as the playfield
    return this.data.tableHeight;
  }

  public getItems(): Array<Item<ItemData>> {
    return Object.values(this.items);
  }
}

/** The four walls around the playfield and the glass. */
function generateOuterBorders(data: TableData): HitObject[] {
  const hitObjects: HitObject[] = [];

  hitObjects.push(
    new LineSeg(
      new Vertex2D(data.right, data.top),
      new Vertex2D(data.right, data.bottom),
      data.tableHeight,
      data.glassHeight,
    ),
  );

  hitObjects.push(
    new LineSeg(
      new Vertex2D(data.left, data.bottom),
      new Vertex2D(data.left, data.top),
      data.tableHeight,
      data.glassHeight,
    ),
  );

  hitObjects.push(
    new LineSeg(
      new Vertex2D(data.right, data.bottom),
      new Vertex2D(data.left, data.bottom),
      data.tableHeight,
      data.glassHeight,
    ),
  );

  hitObjects.push(
    new LineSeg(
      new Vertex2D(data.left, data.top),
      new Vertex2D(data.right, data.top),
      data.tableHeight,
      data.glassHeight,
    ),
  );

  // glass
  const rgv3D: Vertex3D[] = [
    new Vertex3D(data.left, data.top, data.glassHeight),
    new Vertex3D(data.right, data.top, data.glassHeight),
    new Vertex3D(data.right, data.bottom, data.glassHeight),
    new Vertex3D(data.left, data.bottom, data.glassHeight),
  ];
  const ph3dpoly = new Hit3DPoly(rgv3D);
  ph3dpoly.calcHitBBox();
  hitObjects.push(ph3dpoly);

  return hitObjects;
}
